import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { createJobNote, uploadJobNotePhoto } from '../../services/jobNotes';
import { onMutateSuccess } from '../../utils/queryResponse';
import { toast } from '../../utils/toast';

/**
 * JobNoteComposer — general job documentation: a short note, one or more
 * photos, or both, appended to the job's activity timeline (an employee
 * photographing a damaged carpet, a delivery left with a neighbour, …).
 *
 * Photos are picked locally and previewed from object URLs first, so nothing is
 * uploaded until the entry is actually submitted and a mis-picked photo can be
 * dropped for free. On submit each file is compressed in the browser (see
 * compressImage), uploaded on its own, and the returned bucket PATHS are
 * posted with the note — the API only accepts photo references it issued for
 * this same job, so the note cannot be written before its photos have landed.
 *
 * "Visible to customer" (off by default) shares the note AND all its photos
 * on the customer's tracking page — it is one switch per entry on purpose,
 * there is no per-photo sharing.
 *
 * Append-only by design: there is no edit or delete affordance here, the entry
 * is a permanent record of what happened and when.
 *
 * Props: { jobId }
 */
const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';
const MAX_BYTES = 20 * 1024 * 1024;
const MAX_PHOTOS = 20;
const MAX_NOTE = 5000;

// Browser-side compression before upload. Phone cameras produce 3–12 MB
// originals that nobody needs at full size in a job log, and the private
// bucket is billed per byte. Rules:
//   - resize so the LONG edge is at most 1600 px (aspect ratio kept);
//   - re-encode as JPEG at quality 0.8;
//   - a PNG stays a PNG only when it is already small (≤ 300 KB — screenshots
//     with text / transparency), everything else becomes JPEG;
//   - GIFs are left untouched (canvas would drop the animation);
//   - if anything fails (unsupported format, canvas error) the original file
//     is uploaded unchanged — compression is a courtesy, never a blocker.
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.8;
const SMALL_PNG_BYTES = 300 * 1024;

const loadImage = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode failed')); };
    img.src = url;
});

export const compressImage = async (file) => {
    try {
        if (file.type === 'image/gif') return file;
        if (file.type === 'image/png' && file.size <= SMALL_PNG_BYTES) return file;
        const img = await loadImage(file);
        const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height));
        const width = Math.max(1, Math.round((img.naturalWidth || img.width) * scale));
        const height = Math.max(1, Math.round((img.naturalHeight || img.height) * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        // JPEG has no alpha: paint white first so transparent PNG areas do not turn black.
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
        if (!blob) return file;
        // Never make a file bigger than it was.
        if (blob.size >= file.size && scale === 1) return file;
        const name = file.name.replace(/\.[a-z0-9]+$/i, '') + '.jpg';
        return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
    } catch {
        return file;
    }
};

const CameraIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
    </svg>
);
const XIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const JobNoteComposer = ({ jobId }) => {
    const { t } = useTranslation()

    const inputRef = useRef(null)
    const [note, setNote] = useState('')
    // { file, preview } — `preview` is an object URL, revoked the moment the
    // photo is dropped or the entry is submitted so none of them leak.
    const [photos, setPhotos] = useState([])
    const [visibleToCustomer, setVisibleToCustomer] = useState(false)

    const queryClient = useQueryClient()

    const reset = () => {
        setNote('')
        setVisibleToCustomer(false)
        setPhotos((current) => {
            current.forEach((photo) => URL.revokeObjectURL(photo.preview))
            return []
        })
    }

    // Compress + upload every photo first, then write the note with the paths
    // handed back — see the service for why the API takes references rather
    // than bytes here.
    const submitNote = async () => {
        const refs = []
        for (const photo of photos) {
            const response = await uploadJobNotePhoto(jobId, await compressImage(photo.file))
            // A failed upload is returned as the mutation result so the shared
            // handler surfaces the API's own message and no partial note is
            // written; the already-uploaded files are simply left unattached.
            if (response?.code !== 200) return response
            // Bucket path is the stored reference; an older API answers with
            // only the (public) url, which the API still accepts.
            refs.push(response.message.path || response.message.url)
        }
        return createJobNote(jobId, { note: note.trim(), photos: refs, visibleToCustomer })
    }

    const { mutate, isLoading } = useMutation(submitNote, {
        onSuccess: onMutateSuccess(() => {
            queryClient.invalidateQueries(['job-notes', jobId])
            queryClient.invalidateQueries(['job-activities', jobId])
            reset()
        }, t('jobNotes.noteAdded'))
    })

    const handleFiles = (e) => {
        const picked = Array.from(e.target.files || [])
        // Clear the input so picking the same file twice in a row still fires.
        e.target.value = ''

        const accepted = []
        for (const file of picked) {
            if (photos.length + accepted.length >= MAX_PHOTOS) {
                toast.error(t('jobNotes.maxPhotos', { count: MAX_PHOTOS }), { position: toast.POSITION.BOTTOM_RIGHT })
                break
            }
            if (!/^image\/(png|jpe?g|webp|gif)$/.test(file.type)) {
                toast.error(t('jobNotes.invalidImageType', { name: file.name }), { position: toast.POSITION.BOTTOM_RIGHT })
                continue
            }
            if (file.size > MAX_BYTES) {
                toast.error(t('jobNotes.imageTooLarge', { name: file.name }), { position: toast.POSITION.BOTTOM_RIGHT })
                continue
            }
            accepted.push({ file, preview: URL.createObjectURL(file) })
        }

        accepted.length > 0 && setPhotos((current) => [...current, ...accepted])
    }

    const removePhoto = (index) => {
        setPhotos((current) => {
            URL.revokeObjectURL(current[index]?.preview)
            return current.filter((photo, i) => i !== index)
        })
    }

    // Nothing to document — the API rejects a blank note with no photos too.
    const isEmpty = !note.trim() && photos.length === 0

    return (
        <div className="jact-add">
            <textarea className="jact-add-tx" rows={2} maxLength={MAX_NOTE} value={note} disabled={isLoading}
                placeholder={t('jobNotes.placeholder')}
                onChange={(e) => setNote(e.target.value)} />

            {photos.length > 0 &&
                <div className="jact-add-thumbs">
                    {photos.map((photo, index) => (
                        <span className="jact-add-thumb" key={`ph-${index}`}>
                            <img src={photo.preview} alt={photo.file.name} />
                            <button type="button" className="rm" title={t('jobNotes.removePhoto')} aria-label={t('jobNotes.removePhoto')}
                                disabled={isLoading} onClick={() => removePhoto(index)}>
                                <XIcon />
                            </button>
                        </span>
                    ))}
                </div>
            }

            <label className="jact-add-share" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '8px 0 4px', cursor: 'pointer', fontSize: 12.5 }}>
                <input type="checkbox" role="switch" checked={visibleToCustomer} disabled={isLoading}
                    onChange={(e) => setVisibleToCustomer(e.target.checked)} style={{ width: 16, height: 16, flex: 'none', marginTop: 2 }} />
                <span>
                    <b>{t('jobNotes.visibleToCustomer')}</b>
                    <span style={{ display: 'block', fontWeight: 500, color: 'var(--muted, #64748B)', fontSize: 11.5 }}>{t('jobNotes.sharedTogether')}</span>
                </span>
            </label>

            <div className="jact-add-foot">
                <button type="button" className="jact-add-attach" disabled={isLoading} onClick={() => inputRef.current?.click()}>
                    <CameraIcon /> {t('jobNotes.attachPhotos')}
                </button>
                <span className="jact-add-hint">{t('jobNotes.photoHint')}</span>
                <button type="button" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={isEmpty || isLoading} onClick={() => mutate()}>
                    {isLoading ? t('jobNotes.adding') : t('jobNotes.addToActivity')}
                </button>
            </div>

            {/* Left un-`capture`d on purpose: the OS sheet then offers the camera
                *and* the gallery, and multi-select keeps working. */}
            <input ref={inputRef} type="file" accept={ACCEPT} multiple onChange={handleFiles} hidden />
        </div>
    )
}

export default JobNoteComposer
